import { autorun } from "mobx";
import {
  BillingMethodType,
  CancelSubscriptionTypes,
  EditBillingMethodType,
  ItemBillingEnds,
  ItemBillingEndsAfterInterval,
  ItemIncreasePriceBy,
  ItemIncreasePriceInterval,
  PauseDurationTypes,
  PauseSubscriptionOutStandingInvoiceActions,
  RecurringBillingInterval,
  RecurringFeeInterval,
  RefundSubscriptionTypes,
  SubscriptionStatus,
} from "../enums";
import {
  IAPICancelSubscriptionModel,
  IAPICreateSubscriptionAPIModel,
  IAPIEditSubscriptionAPIModel,
  IAPIPauseSubscriptionDetails,
  IAPIPauseSubscriptionModel,
  IAPIResumeSubscriptionModel,
  IAPISubscriptionAdditionalFeesModel,
  IAPISubscriptionDetailModel,
  IAPISubscriptionDetailPageModel,
} from "../interfaces";
import { RelatedInvoicesGridModel } from "./RelatedInvoicesGridModel";
import { OrderRelatedGridModel } from "./OrderRelatedGridModels";
import { RelatedPaymentsGridModel } from "./RelatedPaymentsGridModel";
import { RelatedPaymentMethodGridModel } from "./RelatedPaymentMethodGridModel";
import { ContactReferenceModel } from "./ContactDetailPageModel";
import { PriceCancellationTerms } from "./Price.model";
import {
  CoreInputModelUtil,
  getNewErrors,
  InputErrorModel,
  Merediem,
  nameof,
  UtilityMethods,
} from "@mindmemobile/mm-shared-global";
import { MerchantAccount } from "./MerchantApplicationInputModel";
import { PaymentMethodModel } from "./PaymentMethodModels";
import { PurchasePaymentIntentModel } from "./PurchaseCheckOutDetails";
import { AuthService } from "../services";

export class SubscriptionDetailPageModel {
  detail: SubscriptionDetailModel;
  invoices: RelatedInvoicesGridModel[];
  orders: OrderRelatedGridModel[];
  lastInvoice?: RelatedInvoicesGridModel;
  payments: RelatedPaymentsGridModel[];
  paymentMethods: RelatedPaymentMethodGridModel[];
  totalSubscriptions: number;
  previousRecordId: string;
  nextRecordId: string;
  index: number;
  constructor(data: Partial<SubscriptionDetailPageModel>) {
    this.detail =
      (data.detail && new SubscriptionDetailModel(data.detail)) ||
      new SubscriptionDetailModel({});
    this.invoices =
      data.invoices?.map((item) => new RelatedInvoicesGridModel(item)) || [];
    this.orders =
      data.orders?.map((item) => {
        return new OrderRelatedGridModel(item);
      }) || [];

    const sortedInvoices = this.invoices.sort(
      (a, b) =>
        new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()
    );

    this.lastInvoice =
      sortedInvoices.length > 0 ? sortedInvoices[0] : undefined;
    this.payments =
      data.payments?.map((item) => new RelatedPaymentsGridModel(item)) || [];
    this.paymentMethods =
      data.paymentMethods?.map(
        (item) => new RelatedPaymentMethodGridModel(item)
      ) || [];
    this.totalSubscriptions = data.totalSubscriptions || 0;
    this.previousRecordId = data.previousRecordId || "";
    this.nextRecordId = data.nextRecordId || "";
    this.index = data.index || 0;
  }

  static deserializeAPIModel(
    data: IAPISubscriptionDetailPageModel
  ): SubscriptionDetailPageModel {
    const mappedData: Partial<SubscriptionDetailPageModel> = {
      detail: SubscriptionDetailModel.deserializeAPIModel(data.detail),
      invoices:
        data.invoices?.map((item) =>
          RelatedInvoicesGridModel.deserializeAPIModel(item)
        ) || [],
      orders:
        data.orders?.map((item) =>
          OrderRelatedGridModel.deserializeAPIModel(item)
        ) || [],
      payments:
        data.payments?.map((item) =>
          RelatedPaymentsGridModel.deserializeAPIModel(item)
        ) || [],
      paymentMethods:
        data.paymentMethods?.map((item) =>
          RelatedPaymentMethodGridModel.deserializeAPIModel(item)
        ) || [],
      totalSubscriptions: data.total,
      previousRecordId: data.previousRecordId,
      nextRecordId: data.nextRecordId,
      index: data.index,
    };
    return new SubscriptionDetailPageModel(mappedData);
  }
}

export class SubscriptionDetailModel {
  status: keyof typeof SubscriptionStatus;
  createdDate: Date;
  setupFee?: number;
  amount: number;
  cost?: number;
  description: string;
  id: string;
  contact?: ContactReferenceModel;
  billingInterval: keyof typeof RecurringBillingInterval;
  billingIntervalCount: number;
  lastPaid?: Date;
  cancelOn?: Date;
  additionalFees?: SubcriptionAdditionalFeesModel;
  cancelationTerms?: PriceCancellationTerms;
  nextInvoiceOn?: Date;
  subscriptionNumber?: string;
  pastDueDays: number;
  lastPayment?: RelatedPaymentsGridModel;
  lastInvoice?: RelatedInvoicesGridModel;
  nextInvoice?: RelatedInvoicesGridModel;
  paymentDueDate: Date | null;
  billingEnds: keyof typeof ItemBillingEnds;
  billingEndIntervalCount: number;
  billingEndsAfterInterval: keyof typeof ItemBillingEndsAfterInterval;
  pauseDetails: PauseSubscriptionModel;
  pastDueAmount: number;
  recurringPrice: string;
  billingMethod: keyof typeof BillingMethodType;
  selectedMerchantAccount: MerchantAccount;
  selectedPaymentMethod: PaymentMethodModel;
  constructor(data: Partial<SubscriptionDetailModel>) {
    this.status = data.status || SubscriptionStatus.Active;
    this.createdDate = data.createdDate || new Date();
    this.setupFee = data.setupFee;
    this.amount = data.amount || 0;
    this.cost = data.cost;
    this.description = data.description || "";
    this.paymentDueDate = data.paymentDueDate || null;
    this.id = data.id || "";
    this.contact = data.contact && new ContactReferenceModel(data.contact);
    this.billingInterval =
      data.billingInterval || RecurringBillingInterval.Month;
    this.billingIntervalCount = data.billingIntervalCount || 1;
    this.lastPaid = data.lastPaid;
    this.cancelOn = data.cancelOn;
    this.subscriptionNumber = data.subscriptionNumber || "";
    this.additionalFees =
      data.additionalFees &&
      new SubcriptionAdditionalFeesModel(data.additionalFees);
    this.cancelationTerms =
      data.cancelationTerms &&
      new PriceCancellationTerms(data.cancelationTerms);
    this.nextInvoiceOn = data.nextInvoiceOn;
    this.nextInvoice =
      data.nextInvoice && new RelatedInvoicesGridModel(data.nextInvoice);
    this.pastDueDays = data.pastDueDays || 0;
    this.lastInvoice =
      data.lastInvoice && new RelatedInvoicesGridModel(data.lastInvoice);
    this.lastPayment =
      data.lastPayment && new RelatedPaymentsGridModel(data.lastPayment);
    this.billingEnds = data.billingEnds || "OnCancellation";
    this.billingEndIntervalCount = data.billingEndIntervalCount || 1;
    this.billingEndsAfterInterval =
      (data.billingEndsAfterInterval as keyof typeof ItemBillingEndsAfterInterval) ||
      "Month";
    this.pauseDetails = data.pauseDetails
      ? new PauseSubscriptionModel(data.pauseDetails)
      : new PauseSubscriptionModel();
    this.pastDueAmount = data?.pastDueAmount || 0;
    this.recurringPrice = data?.recurringPrice || "";
    this.billingMethod = data?.billingMethod || "ChargePayment";
    this.selectedMerchantAccount =
      data?.selectedMerchantAccount || new MerchantAccount();
    this.selectedPaymentMethod =
      data?.selectedPaymentMethod || new PaymentMethodModel();
  }

  static deserializeAPIModel(
    data: IAPISubscriptionDetailModel
  ): SubscriptionDetailModel {
    const mappedData: Partial<SubscriptionDetailModel> = {
      status: data.status,
      createdDate: UtilityMethods.removeZFromDateString(data.createdDate),
      setupFee: data.setupFee,
      amount: data.amount,
      cost: data.cost,
      description: data.description,
      id: data.id,
      contact: data.contact
        ? ContactReferenceModel.deserializeAPIModel(data.contact)
        : undefined,
      billingInterval: data.billingInterval,
      billingIntervalCount: data.billingIntervalCount,
      lastPaid: data.lastPaid
        ? UtilityMethods.removeZFromDateString(data.lastPaid)
        : undefined,
      cancelOn: data.cancelOn
        ? UtilityMethods.removeZFromDateString(data.cancelOn)
        : undefined,
      additionalFees:
        data.additionalFees &&
        SubcriptionAdditionalFeesModel.deserializeAPIModel(data.additionalFees),
      cancelationTerms:
        data.cancellationTerms &&
        PriceCancellationTerms.deserializeAPIModel(data.cancellationTerms),
      nextInvoiceOn: data.nextInvoiceOn
        ? UtilityMethods.removeZFromDateString(data.nextInvoiceOn)
        : undefined,
      subscriptionNumber: data.subscriptionNumber,
      pastDueDays: data.pastDueDays || 0,
      lastInvoice:
        data.lastInvoice &&
        RelatedInvoicesGridModel.deserializeAPIModel(data.lastInvoice),
      lastPayment:
        data.lastPayment &&
        RelatedPaymentsGridModel.deserializeAPIModel(data.lastPayment),
      paymentDueDate: data.nextInvoice
        ? UtilityMethods.removeZFromDateString(data.nextInvoice.dueDate)
        : null,
      billingEnds: data.billingEndsInterval,
      billingEndIntervalCount: data.billingEndIntervalCount,
      billingEndsAfterInterval: data.billingEndsAfterInterval,
      pauseDetails:
        data.pauseDetails &&
        PauseSubscriptionModel.deserialize(data.pauseDetails),
      nextInvoice:
        data.nextInvoice &&
        RelatedInvoicesGridModel.deserializeAPIModel(data.nextInvoice),
      pastDueAmount: data.pastDueAmount || 0,
      recurringPrice: UtilityMethods.fixNumber(data.amount || 0),
      billingMethod: data.billingMethod,
      selectedMerchantAccount: data.merchantAccount
        ? new MerchantAccount({
            applicationId: data.merchantAccount.applicationId,
            merchantAccountId: data.merchantAccount.merchantAccountId,
            gateway: data.merchantAccount.gateway,
            name: data.merchantAccount.name,
          })
        : new MerchantAccount(),
      selectedPaymentMethod: data.paymentMethod
        ? PaymentMethodModel.deserializeAPIModel(data.paymentMethod)
        : new PaymentMethodModel(),
    };

    return new SubscriptionDetailModel(mappedData);
  }
}

export class SubcriptionAdditionalFeesModel {
  increasePriceInterval: keyof typeof ItemIncreasePriceInterval;
  increasePriceBy: keyof typeof ItemIncreasePriceBy;
  increasePriceAmount: string;
  recurringFee: string;
  recurringFeeInterval: keyof typeof RecurringFeeInterval;
  errors: InputErrorModel<SubcriptionAdditionalFeesModel>[];
  constructor(data?: Partial<SubcriptionAdditionalFeesModel>) {
    this.increasePriceInterval =
      data?.increasePriceInterval || ItemIncreasePriceInterval.None;
    this.increasePriceBy = data?.increasePriceBy || ItemIncreasePriceBy.Amount;
    this.increasePriceAmount = data?.increasePriceAmount || "";
    this.recurringFee = data?.recurringFee || "";
    this.recurringFeeInterval =
      data?.recurringFeeInterval || RecurringFeeInterval.None;
    this.errors =
      (data &&
        data.errors?.map((e) => {
          return new InputErrorModel<SubcriptionAdditionalFeesModel>(e);
        })) ||
      [];
  }

  createAPIModel(): IAPISubscriptionAdditionalFeesModel {
    return {
      increasePriceInterval: this.increasePriceInterval,
      increasePriceBy: this.increasePriceBy,
      increasePriceAmount:
        this.increasePriceInterval !== ItemIncreasePriceInterval.None &&
        this.increasePriceBy === ItemIncreasePriceBy.Amount
          ? parseFloat(this.increasePriceAmount)
          : 0,
      increasePricePercentage:
        this.increasePriceInterval !== ItemIncreasePriceInterval.None &&
        this.increasePriceBy === ItemIncreasePriceBy.Percent
          ? parseFloat(this.increasePriceAmount)
          : 0,
      fee:
        this.recurringFeeInterval === RecurringFeeInterval.None
          ? 0
          : parseFloat(this.recurringFee),
      feeInterval: this.recurringFeeInterval,
    };
  }

  static deserializeAPIModel(
    data: IAPISubscriptionAdditionalFeesModel
  ): SubcriptionAdditionalFeesModel {
    const mappedData: Partial<SubcriptionAdditionalFeesModel> = {
      increasePriceInterval: data.increasePriceInterval,
      increasePriceBy: data.increasePriceBy,
      increasePriceAmount:
        data.increasePriceBy === ItemIncreasePriceBy.Amount
          ? data.increasePriceAmount > 0
            ? UtilityMethods.fixNumber(data.increasePriceAmount)
            : ""
          : data.increasePricePercentage.toString(),
      recurringFee: data.fee > 0 ? UtilityMethods.fixNumber(data.fee) : "",
      recurringFeeInterval: data.feeInterval,
    };

    return new SubcriptionAdditionalFeesModel(mappedData);
  }

  hasError(field: keyof SubcriptionAdditionalFeesModel): boolean {
    return this.errors.some((e) => e.field === field);
  }

  errorMessage(field: keyof SubcriptionAdditionalFeesModel): string {
    const error = this.errors.find((e) => e.field === field);
    return error ? error?.message || "" : "";
  }

  validate(
    isAutoRun = false,
    recurringPrice?: string,
    isBillEveryGreaterThan3Months?: boolean
  ): boolean {
    const newErrors: InputErrorModel<SubcriptionAdditionalFeesModel>[] = [];

    if (isBillEveryGreaterThan3Months) {
      switch (this.increasePriceInterval) {
        case "None":
          break;
        case "Every6Month":
        case "EveryYear":
        default:
          switch (this.increasePriceBy) {
            case ItemIncreasePriceBy.Amount:
              CoreInputModelUtil.validateDecimalValue(
                this.increasePriceAmount.toString(),
                "increasePriceAmount",
                newErrors,
                undefined,
                true
              );
              if (
                parseFloat(this.increasePriceAmount) >
                parseFloat(recurringPrice || "0")
              ) {
                newErrors.push({
                  field: "increasePriceAmount",
                  message: "Amount can't be greater than recurring price.",
                });
              }
              console.log(newErrors);
              break;
            case ItemIncreasePriceBy.Percent:
              CoreInputModelUtil.validateDecimalValue(
                this.increasePriceAmount.toString(),
                "increasePriceAmount",
                newErrors,
                undefined,
                true
              );
              if (parseFloat(this.increasePriceAmount) > 100) {
                newErrors.push({
                  field: "increasePriceAmount",
                  message:
                    "Increase price percentage cannot be greater than 100.",
                });
              }
              break;
          }
          break;
      }
    } else {
      if (this.increasePriceInterval !== "None") {
        newErrors.push({
          field: "increasePriceInterval",
          message:
            "Price increase cannot be applied if billing interval is less than 3 months.",
        });
      }
    }
    if (isBillEveryGreaterThan3Months) {
      switch (this.recurringFeeInterval) {
        case "None":
          break;
        case "OnRenewal":
        default:
          CoreInputModelUtil.validateDecimalValue(
            this.recurringFee.toString(),
            "recurringFee",
            newErrors,
            0,
            true
          );
          if (
            parseFloat(this.recurringFee) > parseFloat(recurringPrice || "0")
          ) {
            newErrors.push({
              field: "recurringFee",
              message: "Amount can't be greater than recurring price",
            });
          }

          break;
      }
    } else {
      if (this.recurringFeeInterval !== "None") {
        newErrors.push({
          field: "recurringFeeInterval",
          message:
            "Recurring fee cannot be applied if billing interval is less than 3 months.",
        });
      }
    }
    if (isAutoRun) {
      this.errors = getNewErrors(newErrors, this.errors);
    } else {
      this.errors = newErrors;
    }
    return this.errors.length === 0;
  }
}

export class SubscriptionModel {
  id: string;
  contacts?: ContactReferenceModel[];
  selectedContact?: ContactReferenceModel;
  description: string;
  setupFee: string;
  recurringPrice: string;
  recurringCost: string;
  paymentDueDate: Date;
  paymentTime: string;
  paymentMeredium: keyof typeof Merediem;
  billingInterval: keyof typeof RecurringBillingInterval;
  billingIntervalCount: number;
  billingEnds: keyof typeof ItemBillingEnds;
  billingEndIntervalCount: number;
  billingEndsAfterInterval: keyof typeof ItemBillingEndsAfterInterval;
  billingMethod: keyof typeof BillingMethodType;
  editBillingMethod: keyof typeof EditBillingMethodType;
  selectedMerchantAccount: MerchantAccount;
  merchantAccounts: MerchantAccount[];
  selectedPaymentMethod: PaymentMethodModel;
  paymentMethods: PaymentMethodModel[];
  additionalFees: SubcriptionAdditionalFeesModel;
  cancellationTerms: PriceCancellationTerms;
  pastDueDays: number;
  lastPayment?: RelatedPaymentsGridModel;
  lastPaid?: Date;
  lastInvoice?: RelatedInvoicesGridModel;
  status: keyof typeof SubscriptionStatus;
  subscriptionNumber: string;
  isNewPaymentMethod: boolean;
  paymentMethodId: string;
  paymentIntent: PurchasePaymentIntentModel | null;
  newPaymentGatewayMethodId: string;
  errors: InputErrorModel<SubscriptionModel>[];
  hideDueDate: boolean;
  isNewPaymentMethodPrimary: boolean;
  isContactRequired: boolean;

  constructor(data?: Partial<SubscriptionModel>) {
    this.id = data?.id || "";
    this.description = data?.description || "";
    this.setupFee = data?.setupFee || "";
    this.contacts = (data?.contacts || []).map(
      (contact) => new ContactReferenceModel(contact)
    );
    this.selectedContact = data?.selectedContact || new ContactReferenceModel();
    this.recurringPrice = data?.recurringPrice || "";
    this.recurringCost = data?.recurringCost || "";
    this.paymentDueDate = data?.paymentDueDate || new Date();
    this.paymentTime = data?.paymentTime || "";
    this.paymentMeredium = data?.paymentMeredium || Merediem.AM;
    this.billingInterval =
      data?.billingInterval || RecurringBillingInterval.Month;
    this.billingIntervalCount = data?.billingIntervalCount || 0;
    this.billingEnds = data?.billingEnds || "OnCancellation";
    this.paymentMethodId = data?.paymentMethodId || "";
    this.lastPayment =
      data?.lastPayment && new RelatedPaymentsGridModel(data.lastPayment);
    this.paymentIntent = data?.paymentIntent
      ? new PurchasePaymentIntentModel(data.paymentIntent)
      : null;
    this.lastInvoice =
      data?.lastInvoice && new RelatedInvoicesGridModel(data.lastInvoice);
    this.lastPaid = data?.lastPaid;
    this.billingMethod = data?.billingMethod || "ChargePayment";
    this.newPaymentGatewayMethodId = data?.newPaymentGatewayMethodId || "";
    this.editBillingMethod = data?.editBillingMethod || "UseExistingPayment";
    this.merchantAccounts = (data?.merchantAccounts || []).map(
      (account) => new MerchantAccount(account)
    );
    this.selectedMerchantAccount =
      data?.selectedMerchantAccount || new MerchantAccount();
    this.selectedPaymentMethod =
      data?.selectedPaymentMethod || new PaymentMethodModel();
    this.pastDueDays = data?.pastDueDays || 0;
    this.subscriptionNumber = data?.subscriptionNumber || "";
    this.isNewPaymentMethod = data?.isNewPaymentMethod || false;
    this.paymentMethods = (data?.paymentMethods || []).map(
      (paymentMethod) => new PaymentMethodModel(paymentMethod)
    );
    this.additionalFees =
      (data?.additionalFees &&
        new SubcriptionAdditionalFeesModel(data.additionalFees)) ||
      new SubcriptionAdditionalFeesModel();
    this.cancellationTerms =
      (data?.cancellationTerms &&
        new PriceCancellationTerms(data.cancellationTerms)) ||
      new PriceCancellationTerms();
    this.billingEndIntervalCount = data?.billingEndIntervalCount || 0;
    this.billingEndsAfterInterval = data?.billingEndsAfterInterval || "Month";
    this.status = data?.status || "Active";

    this.isNewPaymentMethodPrimary = data?.isNewPaymentMethodPrimary || false;
    this.errors =
      (data &&
        data.errors?.map((e) => {
          return new InputErrorModel<SubscriptionModel>(e);
        })) ||
      [];
    this.isContactRequired = data?.isContactRequired !== false;
    if (!data) {
      this.billingIntervalCount = 1;
      this.billingEndIntervalCount = 1;
      this.billingInterval = RecurringBillingInterval.Month;
      this.paymentDueDate = new Date();
      this.paymentTime = UtilityMethods.getHHMMFromDate(new Date());
      this.paymentMeredium =
        UtilityMethods.getAMPMFromDate(new Date()) === "AM" ? "AM" : "PM";
    }

    this.hideDueDate = this.status == "Cancelled" || this.status == "PastDue";

    autorun(() => {
      this.validate(true);
    });
  }

  hasError(field: keyof SubscriptionModel): boolean {
    return this.errors.some((e) => e.field === field);
  }

  isBillEveryGreaterThan3Months = (
    billingInterval: keyof typeof RecurringBillingInterval,
    billingIntervalCount: number
  ): boolean => {
    if (!billingInterval || !billingIntervalCount) return false;

    const daysPerUnit = {
      [RecurringBillingInterval.Day]: 1,
      [RecurringBillingInterval.Week]: 7,
      [RecurringBillingInterval.Month]: 30,
      [RecurringBillingInterval.Year]: 365,
    };

    const totalDays =
      billingIntervalCount * (daysPerUnit[billingInterval] || 0);

    return totalDays >= 90;
  };

  validate(isAutoRun = false): boolean {
    const newErrors: InputErrorModel<SubscriptionModel>[] = [];
    CoreInputModelUtil.validateTextField(
      this.description,
      "description",
      newErrors
    );
    CoreInputModelUtil.validateTextField(
      this.selectedMerchantAccount.name,
      "selectedMerchantAccount",
      newErrors
    );
    CoreInputModelUtil.validateTime(
      this.paymentTime,
      "paymentTime",
      this.paymentMeredium,
      newErrors
    );
    CoreInputModelUtil.validateTextField(
      this.paymentMeredium,
      "paymentMeredium",
      newErrors
    );
    if (this.isNewPaymentMethod) {
      CoreInputModelUtil.validateTextField(
        this.newPaymentGatewayMethodId,
        "newPaymentGatewayMethodId",
        newErrors
      );
    } else {
      CoreInputModelUtil.validateTextField(
        this.selectedPaymentMethod.id,
        "selectedPaymentMethod",
        newErrors
      );
    }

    if (this.setupFee) {
      CoreInputModelUtil.validateDecimalValue(
        this.setupFee,
        "setupFee",
        newErrors
      );
    }
    // if (this.recurringCost) {
    //     CoreInputModelUtil.validateDecimalValue(this.recurringCost, 'recurringCost', newErrors, 0, true);
    // }

    if (!this.hideDueDate) {
      CoreInputModelUtil.validateDateAndTime(
        nameof<SubscriptionModel>("paymentDueDate"),
        nameof<SubscriptionModel>("paymentTime"),
        this.paymentDueDate,
        this.paymentTime,
        this.paymentMeredium,
        newErrors,
        0
      );
    }

    if (
      !(this.selectedContact?.id || this.selectedContact?.mindmeContactId) &&
      this.isContactRequired
    ) {
      CoreInputModelUtil.addError(
        nameof<SubscriptionModel>("selectedContact"),
        newErrors
      );
    }
    CoreInputModelUtil.validateDecimalValue(
      this.recurringPrice.toString(),
      "recurringPrice",
      newErrors,
      undefined,
      true
    );
    if (!this.billingInterval) {
      newErrors.push({
        field: nameof<SubscriptionModel>("billingInterval"),
      });
    } else {
      switch (this.billingInterval) {
        case RecurringBillingInterval.Month:
          if (this.billingIntervalCount > 12 || this.billingIntervalCount < 1) {
            CoreInputModelUtil.addError(
              nameof<SubscriptionModel>("billingIntervalCount"),
              newErrors
            );
          }
          break;
        case RecurringBillingInterval.Week:
          if (this.billingIntervalCount > 52 || this.billingIntervalCount < 1) {
            CoreInputModelUtil.addError(
              nameof<SubscriptionModel>("billingIntervalCount"),
              newErrors
            );
          }
          break;
        case RecurringBillingInterval.Day:
          if (this.billingIntervalCount > 31 || this.billingIntervalCount < 1) {
            CoreInputModelUtil.addError(
              nameof<SubscriptionModel>("billingIntervalCount"),
              newErrors
            );
          }
          break;
        case RecurringBillingInterval.Year:
          if (this.billingIntervalCount < 1) {
            CoreInputModelUtil.addError(
              nameof<SubscriptionModel>("billingIntervalCount"),
              newErrors
            );
          }
          break;
        default:
          break;
      }
    }
    if (!this.billingEnds) {
      newErrors.push({
        field: nameof<SubscriptionModel>("billingEnds"),
      });
    } else if (this.billingEnds === ItemBillingEnds.After) {
      CoreInputModelUtil.validateDecimalValue(
        this.billingEndIntervalCount.toString(),
        "billingEndIntervalCount",
        newErrors,
        0,
        true
      );
      CoreInputModelUtil.validateTextField(
        this.billingEndsAfterInterval,
        "billingEndsAfterInterval",
        newErrors
      );
    }
    const isValidAdditionalFees = this.additionalFees.validate(
      isAutoRun,
      this.recurringPrice,
      this.isBillEveryGreaterThan3Months(
        this.billingInterval,
        this.billingIntervalCount
      )
    );
    // const isValidCancellationTerms = this.cancellationTerms.validate(isAutoRun);
    if (isAutoRun) {
      this.errors = getNewErrors(newErrors, this.errors);
    } else {
      this.errors = newErrors;
    }
    return this.errors.length === 0 && isValidAdditionalFees;
  }

  createAPIModel(mindmeContactId?: string): IAPICreateSubscriptionAPIModel {
    return {
      contactId: this.selectedContact?.id || "",
      accountId: AuthService.authData.accountId,
      description: this.description,
      billingMethod: this.billingMethod,
      setupFee: this.setupFee === "" ? 0 : parseFloat(this.setupFee),
      price: parseFloat(this.recurringPrice) || 0,
      cost: this.recurringCost === "" ? 0 : parseFloat(this.recurringCost),
      dueDate: UtilityMethods.addTimeToDate(
        new Date(this.paymentDueDate),
        this.paymentTime,
        this.paymentMeredium
      ),
      billingInterval: this.billingInterval,
      billingIntervalCount: this.billingIntervalCount,
      merchantAccount: this.selectedMerchantAccount.createAPIModel2(),
      paymentMethodId: this.selectedPaymentMethod?.id || "",
      cancellationTerms: this.cancellationTerms.createAPIModel(),
      additionalFees: this.additionalFees.createAPIModel(),
      billingEndsInterval: this.billingEnds,
      billingEndIntervalCount: this.billingEndIntervalCount,
      billingEndsAfterInterval: this.billingEndsAfterInterval,
      newPaymentGatewayMethodId: this.isNewPaymentMethod
        ? this.newPaymentGatewayMethodId
        : "",
      isNewPaymentMethodPrimary: this.isNewPaymentMethodPrimary,
      mindmeContactId: mindmeContactId || "",
    };
  }

  createEditAPIModel(): IAPIEditSubscriptionAPIModel {
    return {
      id: this.id,
      ...this.createAPIModel(),
    };
  }

  static deserializeDetailAPIModel(
    apiData: IAPISubscriptionDetailModel
  ): SubscriptionModel {
    // const paymentDueDate = apiData.nextInvoice?.dueDate
    //   ? new Date(
    //       UtilityMethods.removeZFromDateString(apiData.nextInvoice?.dueDate)
    //     )
    //   : new Date();
    // const paymentTime = apiData.nextInvoice?.dueDate
    //   ? UtilityMethods.getHHMMFromDate(paymentDueDate)
    //   : "00:00";
    const paymentDueDate = apiData.dueDate
      ? new Date(UtilityMethods.removeZFromDateString(apiData.dueDate))
      : new Date();
    const paymentTime = apiData.nextInvoice?.dueDate
      ? UtilityMethods.getHHMMFromDate(paymentDueDate)
      : "00:00";
    return new SubscriptionModel({
      id: apiData.id,
      description: apiData.description,
      setupFee: UtilityMethods.fixNumber(apiData.setupFee || 0),
      recurringPrice: UtilityMethods.fixNumber(apiData.amount || 0),
      recurringCost:
        apiData.cost && apiData.cost > 0
          ? UtilityMethods.fixNumber(apiData.cost || 0)
          : "",
      paymentDueDate: paymentDueDate,
      paymentTime: paymentTime,
      paymentMeredium:
        paymentDueDate.getHours() >= 12 ? Merediem.PM : Merediem.AM,
      billingInterval: apiData.billingInterval,
      billingIntervalCount: apiData.billingIntervalCount,
      billingMethod: apiData.billingMethod,
      selectedContact: apiData.contact
        ? ContactReferenceModel.deserializeAPIModel(apiData.contact)
        : new ContactReferenceModel(),
      selectedMerchantAccount: apiData.merchantAccount
        ? new MerchantAccount({
            applicationId: apiData.merchantAccount.applicationId,
            merchantAccountId: apiData.merchantAccount.merchantAccountId,
            gateway: apiData.merchantAccount.gateway,
            name: apiData.merchantAccount.name,
          })
        : new MerchantAccount(),
      selectedPaymentMethod: apiData.paymentMethod
        ? PaymentMethodModel.deserializeAPIModel(apiData.paymentMethod)
        : new PaymentMethodModel(),
      additionalFees: apiData.additionalFees
        ? SubcriptionAdditionalFeesModel.deserializeAPIModel(
            apiData.additionalFees
          )
        : new SubcriptionAdditionalFeesModel(),
      cancellationTerms:
        apiData.cancellationTerms &&
        PriceCancellationTerms.deserializeAPIModel(apiData.cancellationTerms),
      billingEnds: apiData.billingEndsInterval,
      billingEndIntervalCount: apiData.billingEndIntervalCount,
      billingEndsAfterInterval: apiData.billingEndsAfterInterval,
      pastDueDays: apiData.pastDueDays || 0,
      status: apiData.status as keyof typeof SubscriptionStatus,
      lastInvoice:
        apiData.lastInvoice &&
        RelatedInvoicesGridModel.deserializeAPIModel(apiData.lastInvoice),
      lastPaid: apiData.lastPaid
        ? UtilityMethods.removeZFromDateString(apiData.lastPaid)
        : undefined,
      lastPayment:
        apiData.lastPayment &&
        RelatedPaymentsGridModel.deserializeAPIModel(apiData.lastPayment),
      subscriptionNumber: apiData.subscriptionNumber || "",
    });
  }
}

export class CancelSubscriptionModel {
  cancelType: keyof typeof CancelSubscriptionTypes;
  refundType: keyof typeof RefundSubscriptionTypes;
  customRefundDate: Date;
  customAmount: number;
  errors: InputErrorModel<CancelSubscriptionModel>[];
  lastPayment?: RelatedPaymentsGridModel;
  refundPaymentTransactionId: string;
  constructor(data?: Partial<CancelSubscriptionModel>) {
    this.cancelType = data?.cancelType || CancelSubscriptionTypes.Immediately;
    this.refundType = data?.refundType || RefundSubscriptionTypes.None;
    this.customRefundDate = data?.customRefundDate || new Date();
    this.customAmount = data?.customAmount || 0;
    this.errors =
      (data &&
        data.errors?.map((e) => {
          return new InputErrorModel<CancelSubscriptionModel>(e);
        })) ||
      [];
    this.lastPayment =
      data?.lastPayment && new RelatedPaymentsGridModel(data.lastPayment);
    this.refundPaymentTransactionId = data?.refundPaymentTransactionId || "";
    autorun(() => {
      this.validate(true);
    });
  }

  hasError(field: keyof CancelSubscriptionModel): boolean {
    return this.errors.some((e) => e.field === field);
  }

  validate(isAutoRun = false): boolean {
    const newErrors: InputErrorModel<CancelSubscriptionModel>[] = [];
    if (this.cancelType === CancelSubscriptionTypes.OnCustomDate) {
      CoreInputModelUtil.validateDateField(
        "customRefundDate",
        this.customRefundDate.toISOString(),
        newErrors
      );
    }
    if (this.refundType === RefundSubscriptionTypes.CustomAmount) {
      CoreInputModelUtil.validateDecimalValue(
        this.customAmount.toString(),
        "customAmount",
        newErrors,
        0,
        true
      );

      if (this.customAmount > (this.lastPayment?.amount || 0)) {
        CoreInputModelUtil.addError(
          "customAmount",
          newErrors,
          "Refund amount cannot be greater than last payment amount"
        );
      }
    }
    if (isAutoRun) {
      this.errors = getNewErrors(newErrors, this.errors);
    } else {
      this.errors = newErrors;
    }
    return this.errors.length === 0;
  }

  createCancelAPIModel(subscriptionId: string): IAPICancelSubscriptionModel {
    return {
      accountId: AuthService.authData.accountId,
      id: subscriptionId || "",
      cancellationType: this.cancelType,
      customDate:
        this.cancelType === CancelSubscriptionTypes.OnCustomDate
          ? UtilityMethods.removeZFromDateString(
              this.customRefundDate.toISOString()
            )
          : null,
      refundType:
        this.cancelType === CancelSubscriptionTypes.Immediately
          ? this.refundType
          : RefundSubscriptionTypes.None,
      customAmount:
        this.refundType === RefundSubscriptionTypes.CustomAmount
          ? this.customAmount
          : 0,
      refundPaymentTransactionId: this.refundPaymentTransactionId || null,
    };
  }
}

export class PauseSubscriptionModel {
  pauseDurationType: keyof typeof PauseDurationTypes;
  outstandingInvoiceType: keyof typeof PauseSubscriptionOutStandingInvoiceActions;
  resumeDate: Date;
  pausedDate: Date;
  errors: InputErrorModel<PauseSubscriptionModel>[];
  constructor(data?: Partial<PauseSubscriptionModel>) {
    this.pauseDurationType =
      data?.pauseDurationType || PauseDurationTypes.UntilManuallyResumed;
    this.outstandingInvoiceType =
      data?.outstandingInvoiceType ||
      PauseSubscriptionOutStandingInvoiceActions.None;
    this.resumeDate = data?.resumeDate || new Date();
    this.pausedDate = data?.pausedDate || new Date();
    this.errors =
      (data &&
        data.errors?.map((e) => {
          return new InputErrorModel<PauseSubscriptionModel>(e);
        })) ||
      [];
    autorun(() => {
      this.validate(true);
    });
  }

  hasError(field: keyof PauseSubscriptionModel): boolean {
    return this.errors.some((e) => e.field === field);
  }

  validate(isAutoRun = false): boolean {
    const newErrors: InputErrorModel<PauseSubscriptionModel>[] = [];
    if (this.pauseDurationType === PauseDurationTypes.ResumeOnDate) {
      CoreInputModelUtil.validateDateField(
        "resumeDate",
        this.resumeDate.toISOString(),
        newErrors
      );
    }
    if (isAutoRun) {
      this.errors = getNewErrors(newErrors, this.errors);
    } else {
      this.errors = newErrors;
    }
    return this.errors.length === 0;
  }

  static deserialize(
    data: IAPIPauseSubscriptionDetails
  ): PauseSubscriptionModel {
    return new PauseSubscriptionModel({
      pauseDurationType: data.duration,
      resumeDate: UtilityMethods.removeZFromDateString(data.pauseUntil),
      pausedDate: UtilityMethods.removeZFromDateString(data.pauseOn),
    });
  }

  createPauseAPIModel(subscriptionId: string): IAPIPauseSubscriptionModel {
    return {
      accountId: AuthService.authData.accountId,
      Id: subscriptionId || "",
      pauseDurationType: this.pauseDurationType,
      pauseUntil:
        this.pauseDurationType === PauseDurationTypes.ResumeOnDate
          ? UtilityMethods.removeZFromDateString(this.resumeDate.toISOString())
          : null,
      actionOnInvoices: this.outstandingInvoiceType,
    };
  }
}

export class ResumeSubscriptionModel {
  id: string;
  pausedDate: Date | null;
  resumeDate: Date;
  resumeDateTime: string;
  resumeDateMeredium: keyof typeof Merediem;
  billingInterval: keyof typeof RecurringBillingInterval;
  billingIntervalCount: number;
  billingEnds: keyof typeof ItemBillingEnds;
  billingEndIntervalCount: number;
  billingEndsAfterInterval: keyof typeof ItemBillingEndsAfterInterval;
  billingMethod: keyof typeof BillingMethodType;
  editBillingMethod: keyof typeof EditBillingMethodType;
  selectedMerchantAccount: MerchantAccount;
  merchantAccounts: MerchantAccount[];
  selectedPaymentMethod: PaymentMethodModel;
  paymentMethods: PaymentMethodModel[];
  status: keyof typeof SubscriptionStatus;
  isNewPaymentMethod: boolean;
  paymentMethodId: string;
  paymentIntent: PurchasePaymentIntentModel | null;
  newPaymentGatewayMethodId: string;
  errors: InputErrorModel<ResumeSubscriptionModel>[];
  isNewPaymentMethodPrimary: boolean;
  isCollectPastDue: boolean;
  constructor(data?: Partial<ResumeSubscriptionModel>) {
    this.id = data?.id || "";
    this.resumeDate = data?.resumeDate || new Date();
    this.resumeDateTime = data?.resumeDateTime || "";
    this.pausedDate = data?.pausedDate || new Date();
    this.resumeDateMeredium = data?.resumeDateMeredium || Merediem.AM;
    this.billingInterval =
      data?.billingInterval || RecurringBillingInterval.Month;
    this.billingIntervalCount = data?.billingIntervalCount || 0;
    this.billingEnds = data?.billingEnds || "OnCancellation";
    this.paymentMethodId = data?.paymentMethodId || "";
    this.paymentIntent = data?.paymentIntent
      ? new PurchasePaymentIntentModel(data.paymentIntent)
      : null;
    this.billingMethod = data?.billingMethod || "ChargePayment";
    this.newPaymentGatewayMethodId = data?.newPaymentGatewayMethodId || "";
    this.editBillingMethod = data?.editBillingMethod || "UseExistingPayment";
    this.merchantAccounts = (data?.merchantAccounts || []).map(
      (account) => new MerchantAccount(account)
    );
    this.selectedMerchantAccount =
      data?.selectedMerchantAccount || new MerchantAccount();
    this.selectedPaymentMethod =
      data?.selectedPaymentMethod || new PaymentMethodModel();
    this.isNewPaymentMethod = data?.isNewPaymentMethod || false;
    this.paymentMethods = (data?.paymentMethods || []).map(
      (paymentMethod) => new PaymentMethodModel(paymentMethod)
    );
    this.billingEndIntervalCount = data?.billingEndIntervalCount || 0;
    this.billingEndsAfterInterval = data?.billingEndsAfterInterval || "Month";
    this.status = data?.status || "Active";
    this.isNewPaymentMethodPrimary = data?.isNewPaymentMethodPrimary || false;
    this.errors =
      (data &&
        data.errors?.map((e) => {
          return new InputErrorModel<ResumeSubscriptionModel>(e);
        })) ||
      [];

    if (!data) {
      this.billingIntervalCount = 1;
      this.billingEndIntervalCount = 1;
      this.billingInterval = RecurringBillingInterval.Month;
      this.resumeDate = new Date();
      this.resumeDateTime = UtilityMethods.getHHMMFromDate(new Date());
      this.resumeDateMeredium =
        UtilityMethods.getAMPMFromDate(new Date()) === "AM" ? "AM" : "PM";
    }
    this.isCollectPastDue = data?.isCollectPastDue || false;
    autorun(() => {
      this.validate(true);
    });
  }

  hasError(field: keyof ResumeSubscriptionModel): boolean {
    return this.errors.some((e) => e.field === field);
  }

  validate(isAutoRun = false): boolean {
    const newErrors: InputErrorModel<ResumeSubscriptionModel>[] = [];
    CoreInputModelUtil.validateTime(
      this.resumeDateTime,
      "resumeDateTime",
      this.resumeDateMeredium,
      newErrors
    );
    CoreInputModelUtil.validateTextField(
      this.resumeDateMeredium,
      "resumeDateMeredium",
      newErrors
    );
    CoreInputModelUtil.validateTextField(
      this.selectedMerchantAccount.name,
      "selectedMerchantAccount",
      newErrors
    );
    if (this.isNewPaymentMethod) {
      CoreInputModelUtil.validateTextField(
        this.newPaymentGatewayMethodId,
        "newPaymentGatewayMethodId",
        newErrors
      );
    } else {
      CoreInputModelUtil.validateTextField(
        this.selectedPaymentMethod.id,
        "selectedPaymentMethod",
        newErrors
      );
    }
    CoreInputModelUtil.validateDateAndTime(
      nameof<ResumeSubscriptionModel>("resumeDate"),
      nameof<ResumeSubscriptionModel>("resumeDateTime"),
      this.resumeDate,
      this.resumeDateTime,
      this.resumeDateMeredium,
      newErrors,
      0
    );

    if (!this.billingInterval) {
      newErrors.push({
        field: nameof<ResumeSubscriptionModel>("billingInterval"),
      });
    } else {
      switch (this.billingInterval) {
        case RecurringBillingInterval.Month:
          if (this.billingIntervalCount > 12 || this.billingIntervalCount < 1) {
            CoreInputModelUtil.addError(
              nameof<ResumeSubscriptionModel>("billingIntervalCount"),
              newErrors
            );
          }
          break;
        case RecurringBillingInterval.Week:
          if (this.billingIntervalCount > 52 || this.billingIntervalCount < 1) {
            CoreInputModelUtil.addError(
              nameof<ResumeSubscriptionModel>("billingIntervalCount"),
              newErrors
            );
          }
          break;
        case RecurringBillingInterval.Day:
          if (this.billingIntervalCount > 31 || this.billingIntervalCount < 1) {
            CoreInputModelUtil.addError(
              nameof<ResumeSubscriptionModel>("billingIntervalCount"),
              newErrors
            );
          }
          break;
        case RecurringBillingInterval.Year:
        default:
          break;
      }
    }
    if (!this.billingEnds) {
      newErrors.push({
        field: nameof<ResumeSubscriptionModel>("billingEnds"),
      });
    } else if (this.billingEnds === ItemBillingEnds.After) {
      CoreInputModelUtil.validateDecimalValue(
        this.billingEndIntervalCount.toString(),
        "billingEndIntervalCount",
        newErrors,
        0,
        true
      );
      CoreInputModelUtil.validateTextField(
        this.billingEndsAfterInterval,
        "billingEndsAfterInterval",
        newErrors
      );
    }
    if (isAutoRun) {
      this.errors = getNewErrors(newErrors, this.errors);
    } else {
      this.errors = newErrors;
    }
    return this.errors.length === 0;
  }

  static deserialize(data: SubscriptionDetailModel): ResumeSubscriptionModel {
    const resumeDate =
      data.pauseDetails?.resumeDate < new Date()
        ? new Date()
        : data.pauseDetails?.resumeDate;
    const resumeDateTime = resumeDate
      ? UtilityMethods.getHHMMFromDate(resumeDate)
      : "00:00";
    return new ResumeSubscriptionModel({
      id: data.id,
      resumeDate: resumeDate,
      resumeDateTime: resumeDateTime,
      pausedDate: data.pauseDetails?.pausedDate,
      resumeDateMeredium:
        resumeDate.getHours() >= 12 ? Merediem.PM : Merediem.AM,
      billingInterval: data.billingInterval,
      billingIntervalCount: data.billingIntervalCount,
      billingMethod: data.billingMethod,
      selectedMerchantAccount: data.selectedMerchantAccount,
      selectedPaymentMethod: data.selectedPaymentMethod,
      billingEnds: data.billingEnds,
      billingEndIntervalCount: data.billingEndIntervalCount,
      billingEndsAfterInterval: data.billingEndsAfterInterval,
      status: data.status as keyof typeof SubscriptionStatus,
    });
  }

  createResumeAPIModel(): IAPIResumeSubscriptionModel {
    return {
      subscriptionId: this.id,
      accountId: AuthService.authData.accountId,
      billingMethod: this.billingMethod,
      resumeDate: UtilityMethods.addTimeToDate(
        new Date(this.resumeDate),
        this.resumeDateTime,
        this.resumeDateMeredium
      ),
      billingInterval: this.billingInterval,
      billingIntervalCount: this.billingIntervalCount,
      merchantAccount: this.selectedMerchantAccount.createAPIModel2(),
      paymentMethodId: this.selectedPaymentMethod?.id || "",
      billingEndsInterval: this.billingEnds,
      billingEndIntervalCount: this.billingEndIntervalCount,
      billingEndsAfterInterval: this.billingEndsAfterInterval,
      newPaymentGatewayMethodId: this.isNewPaymentMethod
        ? this.newPaymentGatewayMethodId
        : "",
      isNewPaymentMethodPrimary: this.isNewPaymentMethodPrimary,
      isCollectPastDue: this.isCollectPastDue,
    };
  }
}

export function calculateRenewalDate(
  date: Date,
  billingInterval: keyof typeof RecurringBillingInterval,
  billingIntervalCount: number
) {
  var renewalDate = new Date(date);
  switch (billingInterval) {
    case RecurringBillingInterval.Month:
      renewalDate.setMonth(renewalDate.getMonth() + billingIntervalCount);
      break;
    case RecurringBillingInterval.Week:
      renewalDate.setDate(renewalDate.getDate() + billingIntervalCount * 7);
      break;
    case RecurringBillingInterval.Day:
      renewalDate.setDate(renewalDate.getDate() + billingIntervalCount);
      break;
    case RecurringBillingInterval.Year:
      renewalDate.setFullYear(renewalDate.getFullYear() + billingIntervalCount);
      break;
  }
  return renewalDate;
}
